/**
 * Shared rules for the "attach a song" file pickers (landing composer,
 * DirectorHome composer).
 *
 * Two things were turning first-time users away before they ever made a video
 * (2026-07-25 support thread):
 *
 *  1. The cap was 50MB, which is ~5 minutes of WAV. Nothing downstream needs
 *     that: uploads go straight to R2 via a presigned PUT, and the director
 *     re-encodes anything over DIRECTOR_ANALYSIS_MAX_AUDIO_BYTES to a compact
 *     MP3 before analysis. The cap only existed to bound the API relay
 *     fallback, so it now matches that relay's real limit.
 *
 *  2. accept="audio/*" hides .mp4/.mov in the iPadOS Files picker. A lot of
 *     users only have their song as a video (iPad exports, screen recordings,
 *     downloaded clips), so "attach a song" was unreachable on an iPad. We
 *     accept video containers and let the server strip the video stream.
 *
 * Keep MAX_AUDIO_UPLOAD_MB in sync with DIRECT_UPLOAD_MAX_BYTES in
 * server/_core/index.ts.
 */

export const MAX_AUDIO_UPLOAD_MB = 200;
export const MAX_AUDIO_UPLOAD_BYTES = MAX_AUDIO_UPLOAD_MB * 1024 * 1024;

/**
 * iPadOS/iOS report an empty or wrong `file.type` for plenty of files, and the
 * Files picker greys out anything the accept list doesn't match. Listing bare
 * extensions alongside the MIME globs is what actually makes .mp4/.m4a
 * selectable there.
 */
export const AUDIO_UPLOAD_ACCEPT = [
  "audio/*",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  ".mp3",
  ".wav",
  ".m4a",
  ".aac",
  ".ogg",
  ".oga",
  ".opus",
  ".flac",
  ".aiff",
  ".aif",
  ".caf",
  ".mp4",
  ".m4v",
  ".mov",
  ".webm",
].join(",");

// Files that are certainly not a song, even when the browser calls them
// audio/* (MIDI, playlists). Sep 15–29 2026 prod: 47 projects whose "song"
// was a photo, PDF, lyric .txt, .mid backing track, saved web page, APK or
// Audacity project got past the "Attach a song" picker, were created, and
// failed ~80 s later in music analysis (≈3 attempts each).
const NON_AUDIO_TYPE_RE =
  /^(image\/|text\/|application\/(pdf|rtf|json|xml|zip|x-zip-compressed|vnd\.rar|x-rar-compressed|x-7z-compressed|vnd\.android\.package-archive|msword|vnd\.openxmlformats-officedocument\.|vnd\.oasis\.opendocument\.)|audio\/(x-|sp-)?midi?$|application\/x-midi$|(audio|application)\/(x-)?mpegurl$|application\/vnd\.apple\.mpegurl$|audio\/x-scpls$)/i;
const NON_AUDIO_EXTENSION_RE =
  /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?|svg|pdf|txt|rtf|docx?|odt|pages|html?|mid|midi|kar|m3u8?|pls|url|webloc|apk|zip|rar|7z|aup[34]?|json|xml|csv)$/i;

/**
 * Whether a dropped/picked file could be a song (something we can pull audio
 * out of). Rejects only files that are positively not audio — an image, a
 * document, MIDI, a playlist, an archive, an app or project file — by type or
 * by extension. Anything else passes: Safari hands us `""` for iCloud Drive
 * files, Android names some real songs without an extension (~25 such web
 * uploads analyzed fine Sep 15–29 2026), and the analysis step's own byte
 * sniff stays the backstop.
 */
export function isAcceptedAudioSourceFile(file: File): boolean {
  return (
    !NON_AUDIO_TYPE_RE.test(file.type) && !NON_AUDIO_EXTENSION_RE.test(file.name)
  );
}

/**
 * Audio-only variant for tools whose backend cannot demux video (stem
 * splitter / vocal remover feeds the file straight to demucs). Bare
 * extensions are still listed so the iPadOS Files picker does not grey out
 * files with an empty or wrong MIME type.
 */
export const AUDIO_ONLY_UPLOAD_ACCEPT =
  "audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac";

const AUDIO_ONLY_EXTENSION_RE =
  /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|aiff|aif|caf|webm)$/i;

/**
 * Like isAcceptedAudioSourceFile but rejects video containers. Falls back to
 * the extension when `file.type` is empty or not an audio MIME type, which is
 * what iPadOS/iCloud Drive report for many files.
 */
export function isAcceptedAudioOnlyFile(file: File): boolean {
  if (/^audio\//i.test(file.type)) return true;
  if (/^video\//i.test(file.type)) return false;
  return AUDIO_ONLY_EXTENSION_RE.test(file.name);
}
